import { useCallback, useRef, useState } from 'react';

import { FormObject } from '../../@types';
import { ServiceNodeAllocationData, ServiceUpdateRequest } from '../../@types/service.type';
import {
  getNextServiceStatuses,
  saveServiceStatus,
  updateServiceNodeAllocation,
  updateServiceById,
  createServiceLogsForService,
  getServiceById,
} from '../../api/services';

export type DialogType = 'status' | 'assign' | 'schedule' | 'note' | null;

export interface StatusOption { code: string; name: string }

/** Interaction forms captured for a specific job during a status probe. */
export interface JobInteraction {
  serviceId: number;
  forms: FormObject[];
  originalUrl: string;
  originalPayload: Record<string, unknown>;
}

export interface ListJobActionsReturn {
  activeDialog: DialogType;
  targetIds: number[];
  openRowAction: (serviceId: number, action: string) => void;
  openBulkAction: (action: string, ids: number[]) => void;
  closeDialog: () => void;
  statusOptions: StatusOption[];
  statusLoading: boolean;
  submitStatus: (statusCode: string, notes: string) => Promise<void>;
  submitAllocation: (nodeId: number, allocationTypeCode: string) => Promise<void>;
  submitSchedule: (date: string) => Promise<void>;
  submitNote: (note: string) => Promise<void>;
  submitting: boolean;
  lastError: string | null;
  /** Jobs that need interaction forms before their status can change. */
  pendingInteractions: JobInteraction[];
  /** Called after the caller has submitted interaction forms for all pending jobs. */
  clearPendingInteractions: () => void;
  /** Cancel interactions and go back to the status selection dialog. */
  cancelPendingInteractions: () => void;
}

const ACTION_MAP: Record<string, DialogType> = {
  update_status: 'status',
  bulk_update: 'status',
  assign_tech: 'assign',
  schedule: 'schedule',
  add_note: 'note',
};

interface StatusType { code: string; name: string }

function intersectStatuses(allSets: StatusType[][]): StatusOption[] {
  if (allSets.length === 0) return [];
  const first = new Map(allSets[0].map((s) => [s.code, s.name]));
  for (let i = 1; i < allSets.length; i++) {
    const codes = new Set(allSets[i].map((s) => s.code));
    for (const code of first.keys()) { if (!codes.has(code)) first.delete(code); }
  }
  return Array.from(first.entries()).map(([code, name]) => ({ code, name }));
}

export const useListJobActions = (onRefresh?: () => void): ListJobActionsReturn => {
  const [activeDialog, setActiveDialog] = useState<DialogType>(null);
  const [targetIds, setTargetIds] = useState<number[]>([]);
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [pendingInteractions, setPendingInteractions] = useState<JobInteraction[]>([]);
  const refreshRef = useRef(onRefresh);
  refreshRef.current = onRefresh;

  const closeDialog = useCallback(() => {
    setActiveDialog(null);
    setTargetIds([]);
    setStatusOptions([]);
    setLastError(null);
    setPendingInteractions([]);
  }, []);

  const fetchStatusOptions = useCallback(async (ids: number[]) => {
    setStatusLoading(true);
    try {
      const allSets = await Promise.all(
        ids.map(async (id) => {
          try {
            const svc = await getServiceById(id);
            const statusCode = svc.serviceStatusCode ?? svc.serviceStatus?.code ?? '';
            const typeCode = svc.serviceTypeCode ?? '';
            const resp = await getNextServiceStatuses(statusCode, typeCode, id);
            return [...(resp.preferedStatuses ?? []), ...(resp.possibleNextStatuses ?? [])];
          } catch { return []; }
        }),
      );
      setStatusOptions(ids.length === 1
        ? (allSets[0] ?? []).map((s) => ({ code: s.code, name: s.name }))
        : intersectStatuses(allSets));
    } catch { setStatusOptions([]); }
    finally { setStatusLoading(false); }
  }, []);

  const openRowAction = useCallback((serviceId: number, action: string) => {
    const dialog = ACTION_MAP[action] ?? null;
    if (!dialog) return;
    setLastError(null);
    setPendingInteractions([]);
    setTargetIds([serviceId]);
    setActiveDialog(dialog);
    if (dialog === 'status') fetchStatusOptions([serviceId]);
  }, [fetchStatusOptions]);

  const openBulkAction = useCallback((action: string, ids: number[]) => {
    const dialog = ACTION_MAP[action] ?? null;
    if (!dialog) return;
    setLastError(null);
    setPendingInteractions([]);
    setTargetIds(ids);
    setActiveDialog(dialog);
    if (dialog === 'status') fetchStatusOptions(ids);
  }, [fetchStatusOptions]);

  const runForAll = useCallback(async (fn: (id: number) => Promise<unknown>, errorMsg: string) => {
    setSubmitting(true);
    setLastError(null);
    try {
      for (const id of targetIds) await fn(id);
      refreshRef.current?.();
      closeDialog();
    } catch (err) {
      setLastError(err instanceof Error ? err.message : errorMsg);
    } finally {
      setSubmitting(false);
    }
  }, [targetIds, closeDialog]);

  const submitStatus = useCallback(async (statusCode: string, notes: string) => {
    setSubmitting(true);
    setLastError(null);
    const interactions: JobInteraction[] = [];
    try {
      for (const id of targetIds) {
        const payload = {
          nextStatus: statusCode, note: notes,
          byPassInteractionCheck: false, bulkCheckinData: [],
        };
        const result = await saveServiceStatus(
          id, payload as unknown as Parameters<typeof saveServiceStatus>[1],
        ) as unknown as Record<string, unknown>;

        const formData = result.interactionFormData as Array<Record<string, unknown>> | undefined;
        const hasForms = Array.isArray(formData) && formData.length > 0;

        if (hasForms) {
          // Capture interaction forms for this job
          const forms = formData.map((obj) => ({
            ...((obj.templateContent ?? obj) as FormObject),
            ruleId: obj.id as number,
            isMandatory: obj.templateIsMandatory as boolean,
            isRepeatable: obj.templateIsRepeatable as boolean,
          })) as FormObject[];

          interactions.push({
            serviceId: id,
            forms,
            originalUrl: `/services/${id}/status`,
            originalPayload: payload,
          });
        } else {
          // No interactions — bypass to actually change status
          const bypass = { ...payload, byPassInteractionCheck: true };
          await saveServiceStatus(id, bypass as unknown as Parameters<typeof saveServiceStatus>[1]);
        }
      }

      if (interactions.length > 0) {
        // Show interactions to user — don't close dialog yet
        setPendingInteractions(interactions);
        setSubmitting(false);
      } else {
        refreshRef.current?.();
        closeDialog();
        setSubmitting(false);
      }
    } catch (err) {
      setLastError(err instanceof Error ? err.message : 'Status update failed');
      setSubmitting(false);
    }
  }, [targetIds, closeDialog]);

  const clearPendingInteractions = useCallback(() => {
    setPendingInteractions([]);
    refreshRef.current?.();
    closeDialog();
  }, [closeDialog]);

  const cancelPendingInteractions = useCallback(() => {
    setPendingInteractions([]);
  }, []);

  const submitAllocation = useCallback(async (nodeId: number, typeCode: string) => {
    await runForAll((id) => {
      const data: ServiceNodeAllocationData = { serviceId: id, userDepotAllocationNodeId: nodeId, allocationTypeCode: typeCode };
      return updateServiceNodeAllocation(data);
    }, 'Allocation update failed');
  }, [runForAll]);

  const submitSchedule = useCallback(async (date: string) => {
    await runForAll(async (id) => {
      const svc = await getServiceById(id);
      const payload = {
        id,
        serviceTypeCode: svc.serviceTypeCode ?? '',
        serviceStatusCode: svc.serviceStatusCode ?? svc.serviceStatus?.code ?? '',
        actionTypeCode: svc.actionTypeCode ?? '',
        processTypeCode: svc.processTypeCode ?? '',
        nodeId: svc.mainLocationNodeId ?? 0,
        priorityCode: String(svc.priorityCode ?? ''),
        scheduledAt: date,
      };
      await updateServiceById(id, payload as ServiceUpdateRequest);
    }, 'Schedule update failed');
  }, [runForAll]);

  const submitNote = useCallback(async (note: string) => {
    await runForAll(
      (id) => createServiceLogsForService(id, { notes: note }),
      'Failed to add note',
    );
  }, [runForAll]);

  return {
    activeDialog, targetIds, openRowAction, openBulkAction, closeDialog,
    statusOptions, statusLoading, submitStatus, submitAllocation,
    submitSchedule, submitNote, submitting, lastError,
    pendingInteractions, clearPendingInteractions, cancelPendingInteractions,
  };
};
